import React, { useEffect, useRef, useState } from "react";
import { Dropdown, Form, Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";

import DstButton from "../../../../components/DstButton/DstButton";
import ThreeDots from "../../../../components/DstIcons/ThreeDots";
import { useSession } from "../../../../contexts/SessionContext";
import { formatDateHumanly } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import { createProjectNote, deleteProjectNote, fetchProjectNotes, updateProjectNote } from "../../Project.function";
import "./Notes.scss";
import StarRating from "./StarRating/StarRating";

const Notes = () => {
  const { project_id } = useParams();
  const { language, email } = useSession();

  const [notes, setNotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState<number | undefined>();
  const [textValue, setTextValue] = useState("");
  const [editingNote, setEditingNote] = useState<any | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!project_id) return;
    fetchProjectNotes(Number(project_id), setNotes, setLoading, language);
  }, [project_id, language]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [notes]);

  const _handleUpdateNote = () => {
    if (!editingNote || !textValue.trim()) return;

    updateProjectNote(
      Number(project_id),
      editingNote.id,
      {
        content: textValue,
        rating,
        is_pinned: editingNote.is_pinned,
      },
      language,
      (updatedNotes: any[]) => {
        setNotes(updatedNotes);
        setEditingNote(null);
        setTextValue("");
        setRating(undefined);
      }
    );
  };

  const _handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (editingNote) {
      _handleUpdateNote();
      return;
    }

    if (!textValue.trim()) return;

    createProjectNote(
      Number(project_id),
      {
        value: textValue,
        rating,
      },
      setSubmitting,
      (newNotes: any[]) => {
        setNotes(newNotes);
        setTextValue("");
        setRating(undefined);
      },
      language
    );
  };

  const _handleDeleteNote = (noteId: string) => {
    deleteProjectNote(Number(project_id), noteId, language, () => {
      fetchProjectNotes(Number(project_id), setNotes, setLoading, language);
    });
  };

  const _handleTogglePin = (note: any) => {
    updateProjectNote(
      Number(project_id),
      note.id,
      {
        content: note.value,
        rating: note.rating,
        is_pinned: !note.is_pinned,
      },
      language,
      (updatedNotes: any[]) => {
        setNotes(updatedNotes);
      }
    );
  };

  if (!loading) {
    return (
      <div id="project-note-component" className="border p-3">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div id="project-note-component" className="border">
      <div className="d-flex justify-content-between align-items-center">
        <h2 className="my-2 p-3">{translate(language, "PAGES.PROJECT.NOTES.TITLE")}</h2>
      </div>
      <div className="border-top p-3">
        <div className="p-2 tinted-blue-background d-flex justify-content-between align-items-center mb-2">
          <p className="mb-0 xs-regular">{translate(language, "PAGES.PROJECT.NOTES.WARNING_MESSAGE")}</p>
        </div>
        <div id="note-display" className="white-background p-3 d-flex flex-column">
          <div className="scrollable pb-4" ref={scrollContainerRef}>
            {[...notes]
              .sort((a, b) => Number(b.is_pinned) - Number(a.is_pinned))
              .map((note, index) => {
                const isAuthor = note.author === email;

                return (
                  <div
                    key={index}
                    className={`px-3 d-flex ${
                      isAuthor ? "flex-column align-items-end" : "flex-column align-items-start"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      {isAuthor && (
                        <Dropdown className="me-2">
                          <Dropdown.Toggle as="div" className="clickable">
                            <ThreeDots
                              height="0.3em"
                              color="rgba(var(--color-black-rgba), 0.2)"
                              orientation="vertical"
                            />
                          </Dropdown.Toggle>
                          <Dropdown.Menu className="s-regular">
                            <Dropdown.Item
                              onClick={() => {
                                setEditingNote(note);
                                setTextValue(note.value);
                                setRating(note.rating);
                                inputRef.current?.focus();
                              }}
                            >
                              {translate(language, "PAGES.PROJECT.NOTES.MENU.EDIT")}
                              <span className="dst-icon-edit-05 mx-2" />
                            </Dropdown.Item>

                            <Dropdown.Item onClick={() => _handleTogglePin(note)}>
                              {note.is_pinned
                                ? translate(language, "PAGES.PROJECT.NOTES.MENU.UNPIN")
                                : translate(language, "PAGES.PROJECT.NOTES.MENU.PIN")}
                              {note.is_pinned ? (
                                <span className="dst-icon-bookmark-check mx-2" />
                              ) : (
                                <span className="dst-icon-bookmark mx-2" />
                              )}
                            </Dropdown.Item>

                            <Dropdown.Item onClick={() => _handleDeleteNote(note.id)}>
                              {translate(language, "PAGES.PROJECT.NOTES.MENU.DELETE")}
                              <span className="dst-icon-trash-01 mx-2" />
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      )}

                      <div
                        className={`m-regular w-fit p-2 rounded ${
                          isAuthor ? "blue-background white-font" : "light-gray-background"
                        } ${note.is_pinned ? "note-pinned" : ""}`}
                      >
                        {note.rating !== null && note.rating !== undefined && (
                          <div className="mb-1 s-regular">
                            <StarRating value={note.rating} readOnly />
                          </div>
                        )}

                        <p className="my-1">{note.value}</p>
                      </div>
                    </div>
                    <p className="xs-bold mt-1">
                      {translate(language, "PAGES.PROJECT.NOTES.SENT_BY")} {note.author_name ?? note.author}{" "}
                      {translate(language, "PAGES.PROJECT.NOTES.ON_DATE")}{" "}
                      {formatDateHumanly(language, note.dt_created)}
                    </p>
                  </div>
                );
              })}
          </div>

          <Form onSubmit={_handleSubmit} className="d-flex mt-3 mt-auto">
            <Form.Control
              type="text"
              placeholder={translate(language, "PAGES.PROJECT.NOTES.INPUT_PLACEHOLDER")}
              name="notesInput"
              ref={inputRef}
              value={textValue}
              onChange={(event) => setTextValue(event.target.value)}
              className="me-2"
            />

            <div className="align-items-center gap-3 mb-2 me-2">
              <span className="s-regular text-muted">{translate(language, "PAGES.PROJECT.NOTES.RATING")}</span>
              <StarRating value={rating} onChange={setRating} />
            </div>

            <DstButton
              type="submit"
              variant="primary"
              loading={submitting}
              disabled={submitting || !textValue.trim()}
              value={
                editingNote
                  ? translate(language, "PAGES.PROJECT.NOTES.BUTTONS.UPDATE")
                  : translate(language, "PAGES.PROJECT.NOTES.BUTTONS.SUBMIT")
              }
            />
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Notes;
